import { Link, Navigate, Route, Routes } from "react-router-dom";

import { NavHeader } from "@/customer/shell/NavHeader";
import { Footer } from "@/customer/shell/Footer";
import { SkipLink } from "@/customer/shell/SkipLink";
import { HomePage } from "@/customer/catalog/HomePage";
import { CategoryLandingPage } from "@/customer/catalog/CategoryLandingPage";
import { TypeDetailPage } from "@/customer/catalog/TypeDetailPage";
import { SearchResultsPage } from "@/customer/catalog/SearchResultsPage";
import { SignInPage } from "@/customer/auth/SignInPage";
import { SignUpPage } from "@/customer/auth/SignUpPage";
import { PasswordResetPage } from "@/customer/auth/PasswordResetPage";
import { PasswordEditPage } from "@/customer/auth/PasswordEditPage";
import { MagicLinkConsumePage } from "@/customer/auth/MagicLinkConsumePage";
import { AccountPage } from "@/customer/account/AccountPage";
import { ProfileTab } from "@/customer/account/ProfileTab";
import { AddressesTab } from "@/customer/account/AddressesTab";
import { SecurityTab } from "@/customer/account/SecurityTab";
import { AgreementSignerPage } from "@/customer/checkout/agreement/AgreementSignerPage";
import { CartPage } from "@/customer/cart/CartPage";
import { CoiPage } from "@/customer/coi/CoiPage";
import { PortalLayout } from "@/customer/portal/PortalLayout";
import { DashboardPage as PortalDashboardPage } from "@/customer/portal/DashboardPage";
import { OrderDetailPage as PortalOrderDetailPage } from "@/customer/portal/OrderDetailPage";
import { ReorderFlowPage as PortalReorderFlowPage } from "@/customer/portal/ReorderFlowPage";
import { ProfilePage as PortalProfilePage } from "@/customer/portal/ProfilePage";
import { BillingPage as PortalBillingPage } from "@/customer/portal/BillingPage";
import { IdsPage as PortalIdsPage } from "@/customer/portal/IdsPage";

// Customer-facing SPA shell. Sticky NavHeader on top, scrollable main
// content, Footer at the bottom. Each route renders inside `<main>`
// so the SkipLink target is consistent and screen readers always land
// in the same landmark on navigation.
export function CustomerApp() {
  return (
    <div className="flex min-h-screen flex-col bg-zinc-50 text-brand-black">
      <SkipLink />
      <NavHeader />
      <main
        id="main-content"
        className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 md:px-6 md:py-8"
      >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/c/:category_slug" element={<CategoryLandingPage />} />
          <Route path="/c/:category_slug/:type_slug" element={<TypeDetailPage />} />
          <Route path="/search" element={<SearchResultsPage />} />

          {/* Phase 4e CO-only storefront FR-3 — the public /locations
              page is removed (single operating region in v1). Any
              inbound link or browser bookmark redirects home rather
              than 404ing. Restore a real page if/when NV is enabled. */}
          <Route path="/locations" element={<Navigate to="/" replace />} />

          <Route path="/cart" element={<CartPage />} />

          {/* Phase 2c single-page checkout lands in a follow-up slice.
              The placeholder lets the cart's "Continue to checkout" CTA
              navigate somewhere meaningful in the meantime. */}
          <Route path="/checkout" element={<CheckoutPlaceholder />} />

          {/* Phase 2a customer-accounts — standalone auth surfaces. The
              inline-checkout flow (2c) reuses the same APIs but lives
              inside the checkout shell. */}
          <Route path="/sign-in" element={<SignInPage />} />
          <Route path="/sign-up" element={<SignUpPage />} />
          <Route path="/password/reset" element={<PasswordResetPage />} />
          <Route path="/password/edit" element={<PasswordEditPage />} />
          <Route path="/login/magic" element={<MagicLinkConsumePage />} />

          <Route path="/account" element={<AccountPage />}>
            <Route index element={<ProfileTab />} />
            <Route path="addresses" element={<AddressesTab />} />
            <Route path="security" element={<SecurityTab />} />
            {/* Phase 2b coi-upload-and-review — COI tab. 2c checkout will
                also embed the underlying <CoiUploader/> in the cart flow. */}
            <Route path="coi" element={<CoiPage />} />
          </Route>

          {/* Phase 2a rental-agreement — full-page signer hosted at
              /checkout/agreement/:orderId. 2c will embed the underlying
              <AgreementSigner> inside the checkout shell. */}
          <Route path="/checkout/agreement/:orderId" element={<AgreementSignerPage />} />

          {/* Phase 4a customer-portal — logged-in portal under /portal.
              PortalLayout owns the header, delinquency banner, and the
              401→/sign-in redirect; pages render via <Outlet />. */}
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalDashboardPage />} />
            <Route path="orders/:order_number" element={<PortalOrderDetailPage />} />
            <Route path="reorder/:order_number" element={<PortalReorderFlowPage />} />
            <Route path="profile" element={<PortalProfilePage />} />
            <Route path="account/billing" element={<PortalBillingPage />} />
            <Route path="account/ids" element={<PortalIdsPage />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function CheckoutPlaceholder(): JSX.Element {
  return (
    <section className="rounded-md border border-zinc-200 bg-brand-white p-6">
      <h1 className="text-xl font-semibold text-brand-black">Checkout</h1>
      <p className="mt-2 text-sm text-zinc-600">
        The single-page checkout (address, dates, account, agreement,
        payment) lands in the next 2c slice. Your cart is preserved
        until then.
      </p>
      <Link to="/cart" className="mt-3 inline-block text-sm text-brand-red underline">
        Back to cart
      </Link>
    </section>
  );
}

function NotFound(): JSX.Element {
  return (
    <section className="rounded-lg border border-dashed border-zinc-300 p-8 text-center">
      <h1 className="text-lg font-semibold text-brand-black">Page not found</h1>
      <p className="mt-2 text-sm text-zinc-600">
        We don&rsquo;t have a page at that URL. Browse from the home page or{" "}
        <a className="text-brand-red underline" href="tel:+13034354328">
          call us at 303-435-4328
        </a>
        .
      </p>
      <Link to="/" className="mt-3 inline-block text-brand-red underline">
        Back to home
      </Link>
    </section>
  );
}
